import { useTracking } from 'app/tracking/TrackingProvider';
import { Button } from 'features/components/Button';
import { Dialog } from 'features/entry-form-v2/component/Dialog';
import { useLanguage } from 'features/hooks/useLanguage';
import { useLocalStorage } from 'features/hooks/useLocalStorage';
import { useEffect, useState } from 'react';
import { LanguageType } from 'types';
import { useIsSurveyFeatureEnabled } from '../data/useIsSurveyEnabled';

export const SURVEY_INTRO_DIALOG_KEY = 'has_seen_survey_intro_dialog';

export const SurveyIntroDialog = () => {
	const [hasSeenSurveyIntro, setHasSeenSurveyIntro] = useLocalStorage(
		SURVEY_INTRO_DIALOG_KEY,
		false
	);
	const surveyEnabled = useIsSurveyFeatureEnabled();
	const language = useLanguage();
	const translation = DICTIONARY[language];
	const { track } = useTracking();

	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (hasSeenSurveyIntro || !surveyEnabled) return;

		setIsOpen(true);
	}, [hasSeenSurveyIntro, surveyEnabled]);

	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const currentStep = STEPS[currentStepIndex];

	const closeDialog = () => {
		setIsOpen(false);
		setHasSeenSurveyIntro(true);
	};

	return (
		<Dialog
			title={translation.title}
			open={isOpen}
			onClose={() => {
				track({
					eventName: 'survey-intro-dialog-closed'
				});
				closeDialog();
			}}
		>
			<div className="flex flex-col gap-4 w-[600px] h-[600px]">
				{currentStep === 'intro' && <IntroStep />}
				{currentStep === 'enable-survey' && <EnableSurveyStep />}
				{currentStep === 'create-invitation' && <CreateInvitationStep />}
				{currentStep === 'survey-status' && <SurveyStatusStep />}
				{currentStep === 'respond-to-survey' && <RespondToSurveyStep />}
			</div>

			<div className="flex flex-row gap-4 items-end justify-between">
				<div className="text-sm">
					{translation.step_x_of_total(currentStepIndex + 1, STEPS.length)}
				</div>

				<div className="flex flex-row gap-4">
					{currentStepIndex > 0 && (
						<Button
							text={translation.previous}
							variant="secondary"
							onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
						/>
					)}

					{currentStepIndex < STEPS.length - 1 && (
						<Button
							text={translation.next}
							variant="secondary"
							onClick={() => {
								setCurrentStepIndex(currentStepIndex + 1);
								track({
									eventName: `survey_intro-${currentStep}_completed`
								});
							}}
						/>
					)}

					{currentStepIndex === STEPS.length - 1 && (
						<Button
							text={translation.done}
							variant="primary"
							onClick={() => {
								closeDialog();
								track({
									eventName: 'survey-intro-dialog-completed'
								});
							}}
						/>
					)}
				</div>
			</div>
		</Dialog>
	);
};

const IntroStep = () => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	return (
		<>
			<p className="text-base">{translation.introDescription}</p>

			<p className="text-base">{translation.introDescription2}</p>

			<p className="text-base">{translation.introDescription3}</p>
		</>
	);
};

const EnableSurveyStep = () => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	return (
		<>
			<p className="text-base">{translation.enableSurveyDescription}</p>

			<img
				src="/img/survey-changelog/intro/01-enable-survey.gif"
				alt="Enable survey for series"
				className="mt-6 w-full"
			/>
		</>
	);
};

const CreateInvitationStep = () => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	return (
		<>
			<p className="text-base">{translation.createInvitationDescription}</p>

			<img
				src="/img/survey-changelog/intro/02-create-invitation.gif"
				alt="Create survey invitation"
				className="mt-6 w-full"
			/>
		</>
	);
};

const SurveyStatusStep = () => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	return (
		<>
			<p className="text-base">{translation.surveyStatusDescription}</p>

			<img
				src="/img/survey-changelog/intro/03-survey-status.gif"
				alt="See status of survey invitations"
				className="mt-6 w-full"
			/>
		</>
	);
};

const RespondToSurveyStep = () => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	return (
		<>
			<p className="text-base">{translation.respondToSurveyDescription}</p>

			<img
				src="/img/survey-changelog/intro/04-respond-to-survey.gif"
				alt="Respond to survey"
				className="mt-6 w-full"
			/>
		</>
	);
};

const STEPS = ['intro', 'enable-survey', 'create-invitation', 'survey-status', 'respond-to-survey'];

const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Survey in Ledidi',
		step_x_of_total: (step: number, total: number) => `Step ${step} of ${total}`,
		previous: 'Previous',
		next: 'Next',
		done: 'Done',
		introDescription:
			'Survey makes it possible to send a form to a patient without a Ledidi account for filling out data in a project.',
		introDescription2:
			'It works by generating an unique link and a pin code that is sent to the patient. A new entry in a series is created when the survey is completed.',
		introDescription3: 'Click next to see how.',
		enableSurveyDescription:
			'Go to the variables page, and then to the settings for the series you want to add data to through surveys. Go to the user guide if you need help with how series work.',
		createInvitationDescription:
			'Click on the entry that you want to create the survey invitation for. You need to send the link and the pin code to the patient. It is recommended to send the link over email and the pin code on SMS.',
		surveyStatusDescription:
			'Click on the status icon to get an overview of the surveys for a specific patient. It is possible to copy the link and generate a new pin code if necessary.',
		respondToSurveyDescription: 'This is how it looks for a patient completing a survey.'
	},
	[LanguageType.Norwegian]: {
		title: 'Utsendelse av skjema i Ledidi',
		step_x_of_total: (step: number, total: number) => `Steg ${step} of ${total}`,
		previous: 'Forrige',
		next: 'Neste',
		done: 'Ferdig',
		introDescription:
			'Undersøkelser gjør det mulig å sende et skjema til en pasient uten en Ledidi-konto for å fylle ut data i et prosjekt.',
		introDescription2:
			'Det fungerer ved å generere en unik lenke og en pin-kode som sendes til pasienten. En ny inngang i en serie opprettes når undersøkelsen er fullført.',
		introDescription3: 'Klikk neste for å se hvordan.',
		enableSurveyDescription:
			'Gå til variablene, og deretter til innstillingene for serien du vil legge til data gjennom undersøkelser. Les brukerguide hvis du trenger hjelp med hvordan serien fungerer.',
		createInvitationDescription:
			'Klikk på inngangen du vil lage undersøkelsesinvitasjon for. Du må sende lenken og pin-koden til pasienten. Det anbefales å sende lenken via e-post og pin-koden via SMS.',
		surveyStatusDescription:
			'Klikk på statusikonet for å få en oversikt over undersøkelsesinvitasjonene for en spesifikk pasient. Det er mulig å kopiere lenken og generere en ny pin-kode hvis det er nødvendig.',
		respondToSurveyDescription:
			'Dette er hvordan det ser ut for en pasient som fullfører en undersøkelse.'
	}
};
